import * as types from '../types';
import { Series } from '../types/Series';
import { expectUnreachable } from './util';
import i18next from '../../i18n';

export const MARTINGALE_BAND_PRODUCT_ID = 'martingale-band';

export function allSkusForProduct(product: types.IProduct) {
  if (product.category === types.ProductCategory.BAND || product.category === types.ProductCategory.COLLAR_KIT) {
    return product.variants.map((variant) => variant.sku);
  }

  return [product.sku];
}

export function getProductsAndVariantsBySku(
  products: types.IProduct[],
): Map<string, { product: types.IProduct; variant?: types.IVariant }> {
  const result: Map<string, { product: types.IProduct; variant?: types.IVariant }> = new Map();

  for (const product of products) {
    if (product.category === types.ProductCategory.BAND || product.category === types.ProductCategory.COLLAR_KIT) {
      for (const variant of product.variants) {
        result.set(variant.sku, { product, variant });
      }
    } else {
      result.set(product.sku, { product });
    }
  }

  return result;
}

export function getProductsBySku(products: types.IProduct[]) {
  const productsBySku: Map<string, types.IProduct> = new Map();

  for (const product of products) {
    for (const sku of allSkusForProduct(product)) {
      productsBySku.set(sku, product);
    }
  }

  return productsBySku;
}

const bandMakerNames: Record<types.BandMaker, () => string> = {
  [types.BandMaker.Fi]: () => i18next.t('band_maker_fi'),
  [types.BandMaker.LandsharkSupply]: () => i18next.t('band_maker_landshark_supply'),
  [types.BandMaker.MimiGreen]: () => i18next.t('band_maker_mimi_green'),
  [types.BandMaker.RopeHounds]: () => i18next.t('band_maker_rope_hounds'),
  [types.BandMaker.StuntPuppy]: () => i18next.t('band_maker_stunt_puppy'),
  [types.BandMaker.ZeeDog]: () => i18next.t('band_maker_zee_dog'),
};

export function nameForBandMaker(maker: types.BandMaker): string {
  return bandMakerNames[maker]();
}

const colorOptionNames: Record<types.ColorOption, () => string> = {
  [types.ColorOption.Yellow]: () => i18next.t('color_yellow'),
  [types.ColorOption.YellowScribble]: () => i18next.t('color_yellow_with_pattern_out'),
  [types.ColorOption.Gray]: () => i18next.t('color_gray'),
  [types.ColorOption.GrayScribble]: () => i18next.t('color_gray_with_pattern_out'),
  [types.ColorOption.Blue]: () => i18next.t('color_blue'),
  [types.ColorOption.BlueScribble]: () => i18next.t('color_blue_with_pattern_out'),
  [types.ColorOption.Pink]: () => i18next.t('color_pink'),
  [types.ColorOption.PinkScribble]: () => i18next.t('color_pink_with_pattern_out'),
  [types.ColorOption.Strava]: () => i18next.t('color_limited_edition_strava_collar'),
  [types.ColorOption.LandsharkSupplyBlackBlue]: () => i18next.t('color_black_blue'),
  [types.ColorOption.LandsharkSupplyBlackRed]: () => i18next.t('color_black_red'),
  [types.ColorOption.MimiGreenPinkReflective]: () => i18next.t('color_pink_reflective'),
  [types.ColorOption.MimiGreenRedBiothane]: () => i18next.t('color_red_biothane'),
  [types.ColorOption.RopeHoundsBubblegumPop]: () => i18next.t('color_bubblegum_pop'),
  [types.ColorOption.RopeHoundsMountainTop]: () => i18next.t('color_mountain_top'),
  [types.ColorOption.StuntPuppyFishtailTeal]: () => i18next.t('color_fishtail_teal_everyday'),
  [types.ColorOption.StuntPuppyFloraFrolic]: () => i18next.t('color_flora_frolic_fall_everyday'),
  [types.ColorOption.StuntPuppyGoDog]: () => i18next.t('color_go_dog_glo_dry'),
  [types.ColorOption.StuntPuppyPinesBlue]: () => i18next.t('color_pines_blue_everyday'),
  [types.ColorOption.StuntPuppyTealBiothane]: () => i18next.t('color_teal_biothane_dry'),
  [types.ColorOption.ZeeDogPhantom]: () => i18next.t('color_phantom'),
};

export function colorAndDesignVariantName(color: types.ColorOption): string {
  return colorOptionNames[color]();
}

/**
 * For Fi Series 1 and 2 bands, we have a special case for how we want to display the pink color. Also, for Fi band
 * colors, this method is meant to display color without the scribble in vs. out pattern distinction.
 */
export function colorVariantName(color: types.ColorOption, bandSeries: types.BandSeries) {
  if (color === types.ColorOption.Yellow || color === types.ColorOption.YellowScribble) {
    return i18next.t('color_yellow');
  } else if (color === types.ColorOption.Gray || color === types.ColorOption.GrayScribble) {
    return i18next.t('color_gray');
  } else if (color === types.ColorOption.Blue || color === types.ColorOption.BlueScribble) {
    return i18next.t('color_blue');
  } else if (color === types.ColorOption.Pink || color === types.ColorOption.PinkScribble) {
    return bandSeries === types.BandSeries.F1 ? i18next.t('color_pink_ombre') : i18next.t('color_pink');
  } else {
    // For all other colors we can fallback to the full name
    return colorAndDesignVariantName(color);
  }
}

export function colorVariantDescription(color: types.ColorOption): string | undefined {
  if (color === types.ColorOption.ZeeDogPhantom) {
    return i18next.t('color_description_zee_dog_phantom');
  } else if (color === types.ColorOption.StuntPuppyGoDog || color === types.ColorOption.StuntPuppyTealBiothane) {
    return i18next.t('color_description_stunt_puppy_bothane');
  } else if (
    color === types.ColorOption.StuntPuppyFishtailTeal ||
    color === types.ColorOption.StuntPuppyFloraFrolic ||
    color === types.ColorOption.StuntPuppyPinesBlue
  ) {
    return i18next.t('color_description_stunt_puppy_everyday');
  } else if (color === types.ColorOption.MimiGreenRedBiothane) {
    return i18next.t('color_description_mimi_green_red_biothane');
  } else if (color === types.ColorOption.MimiGreenPinkReflective) {
    return i18next.t('color_description_mimi_green_pink_reflective');
  } else if (color === types.ColorOption.RopeHoundsMountainTop || color === types.ColorOption.RopeHoundsBubblegumPop) {
    return i18next.t('color_description_rope_hounds');
  } else if (
    color === types.ColorOption.LandsharkSupplyBlackBlue ||
    color === types.ColorOption.LandsharkSupplyBlackRed
  ) {
    return i18next.t('color_description_landshark_supply');
  } else {
    return '';
  }
}

export function sizeName(size: types.SizeOption) {
  if (size === types.SizeOption.XSmall) {
    return i18next.t('size_xs');
  } else if (size === types.SizeOption.Small) {
    return i18next.t('size_s');
  } else if (size === types.SizeOption.Medium) {
    return i18next.t('size_m');
  } else if (size === types.SizeOption.Large) {
    return i18next.t('size_l');
  } else if (size === types.SizeOption.XLarge) {
    return i18next.t('size_xl');
  } else {
    expectUnreachable(size);
    return size;
  }
}

export function variantDescriptionForAnalytics(variant: types.IVariant) {
  return `${variant.options.size} ${variant.options.color}`;
}

function partialVariantOptionsMatch(l: Partial<types.IVariantOptions>, r: types.IVariantOptions) {
  return (
    (l.color === undefined || l.color === r.color) &&
    (l.size === undefined || l.size === r.size) &&
    (l.collarType === undefined || l.collarType === r.collarType)
  );
}

export function variantWithOptions(
  variants: types.IVariant[],
  options: Partial<types.IVariantOptions>,
): types.IVariant | undefined {
  return variants.find((v) => partialVariantOptionsMatch(options, v.options));
}

/**
 * @returns Whether two variant.option objects are equal
 */
export function variantOptionsEqual(l: Partial<types.IVariantOptions>, r: Partial<types.IVariantOptions>): boolean {
  return l.color === r.color && l.size === r.size && l.collarType === r.collarType;
}

export function exactVariantForOptions(
  variants: types.IVariant[],
  options: Partial<types.IVariantOptions>,
): types.IVariant | undefined {
  return variants.filter((v) => variantOptionsEqual(v.options, options))[0];
}

export function bandSeriesToCollarSeries(bandSeries: types.BandSeries) {
  if (bandSeries === types.BandSeries.F1) {
    return Series.Series2;
  } else if (bandSeries === types.BandSeries.F3) {
    return Series.Series3;
  } else {
    expectUnreachable(bandSeries);
    throw new Error(i18next.t('unknown_band_series', { bandSeries }));
  }
}

export function checkoutImageUrlForProduct(productId: string) {
  if (productId === 'supplements-multivitamin') {
    return '/product_images/supplements/upsell-v2/formula-bag.png';
  } else if (productId === 'supplements-skin-coat') {
    return '/product_images/supplements/upsell-v2/skincoat-bag.png';
  } else if (productId === 'supplements-hip-joint') {
    return '/product_images/supplements/upsell-v2/hipjoint-bag.png';
  } else if (productId === 'supplements-calming') {
    return '/product_images/supplements/upsell-v2/calming-bag.png';
  } else if (productId === 'smart-collar-mini') {
    return '/product_images/smart-collar-mini.png';
  } else if (productId === 'smart-collar-ultra') {
    return '/product_images/smart-collar-ultra.png';
  } else if (productId === 'additional-base-s3') {
    return '/product_images/additional-base-s3.png';
  }
  return `/product_images/${productId}.jpg`;
}

export function checkoutImageUrlForProductWithVariant(productId: string, variant: types.IVariant) {
  return `/product_images/${productId}/${variant.options.color.replace(/[\s/]/g, '_')}.png`;
}

/** The checkout image as an absolute URL on `origin`, for payloads read off-site (emails, vendor tags). */
export function absoluteCheckoutImageUrl(productId: string, variant: types.IVariant | undefined, origin: string) {
  return new URL(
    variant ? checkoutImageUrlForProductWithVariant(productId, variant) : checkoutImageUrlForProduct(productId),
    origin,
  ).toString();
}
